import { useTranslation } from 'react-i18next';
import { TeeButton } from '@/components/ui/tee-button';
import { useAssistantSessions } from '@/hooks/useAssistantSessions';

export interface AssistantRecentListProps {
  limit: number;
  onSelect?: (sessionId: string) => void;
  heading?: string;
}

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['day', 86_400_000],
  ['hour', 3_600_000],
  ['minute', 60_000],
];

export function relativeDate(iso: string | undefined, lang: string): string {
  if (!iso) return '';
  const diff = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(diff)) return '';
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
  for (const [unit, ms] of UNITS) {
    if (Math.abs(diff) >= ms) return rtf.format(Math.round(diff / ms), unit);
  }
  return rtf.format(0, 'minute');
}

/** Recent assistant threads; reused by the empty state and the history view. */
export function AssistantRecentList({ limit, onSelect, heading }: AssistantRecentListProps) {
  const { t, i18n } = useTranslation();
  const { list } = useAssistantSessions(1, limit);
  const sessions = (list.data?.data ?? []).slice(0, limit);
  if (sessions.length === 0) return null;

  return (
    <div className="flex flex-col">
      <p className="pb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-content-sub-title)]">
        {heading ?? t('assistant.empty.recent')}
      </p>
      {sessions.map((s) => (
        <div key={s.id} className="border-t border-[var(--border-default)]">
          <TeeButton
            variant="ghost"
            size="s"
            className="min-h-[40px] w-full justify-between"
            onClick={() => onSelect?.(s.id)}
            text={
              <span className="flex w-full items-center justify-between gap-3">
                <span className="truncate text-[var(--text-content-title)]">
                  {s.title || t('assistant.panel.untitled')}
                </span>
                <span className="shrink-0 text-[12px] text-[var(--text-content-sub-title)]">
                  {relativeDate(s.lastMessageAt ?? s.updatedAt, i18n.language)}
                </span>
              </span>
            }
          />
        </div>
      ))}
    </div>
  );
}
